import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { fetchProject } from "../../store/slices/projectSlice";
import { AppDispatch, RootState } from "../../store";
import ExploreMoreProjects from "./components/ExploreMoreProjects";
import ProjectDetailsHeader from "./components/ProjectDetailsHeader";
import ProjectGallery from "./components/ProjectGallery";
import ProjectInfo from "./components/ProjectInfo";
import LoaderWithContainer from "../../components/base-components/LoaderWithContainer";
import ErrorMessageWithContainer from "../../components/base-components/ErrorWithConatiner";
import NoData from "../../components/base-components/NoData";
import MobileProjectGallery from "./components/MobleProjectGallery";
import DevelopmentUpdatesBtn from "./components/DevelopmentUpdatesBtn";
import { Helmet } from "react-helmet-async";

const ProjectDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const { project, status, error } = useSelector(
    (state: RootState) => state.project
  );

  const isMobile = useMediaQuery({ maxWidth: 1024 });

  useEffect(() => {
    if (id) {
      dispatch(fetchProject(id));
    }
  }, [id, dispatch]);

  if (status === "loading") return <LoaderWithContainer />;
  if (status === "failed") return <ErrorMessageWithContainer message={error} />;
  if (!project) {
    return <NoData message="Project Not Found" />;
  }

  return (
    <>
      {project && (
        <>
          <Helmet>
            <title>
              {project.attributes.seo_title || project.attributes.title} -
              DanCap Group
            </title>
            {project.attributes.seo_description && (
              <meta
                name="description"
                content={project.attributes.seo_description}
              />
            )}

            <link rel="canonical" href={window.location.href} />
            <meta
              property="og:image"
              content="https://www.dancap-group.co.uk/images/dancap-logo-dark.jpg"
            />
            <meta property="og:image:type" content="image/jpeg" />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />

            <meta
              property="og:title"
              content={` ${
                project.attributes.seo_title || project.attributes.title
              } -
              DanCap Group`}
            />
            <meta
              property="og:description"
              content={`${
                project.attributes.seo_description
                  ? project.attributes.seo_description
                  : "Property development and investment company specialising in high-quality residential and commercial projects"
              }`}
            />
            <meta property="og:url" content={window.location.href} />
            <meta property="og:type" content="website" />

            <meta name="twitter:card" content="summary_large_image" />
            <meta
              name="twitter:image"
              content="https://www.dancap-group.co.uk/images/dancap-logo-dark.jpg"
            />
          </Helmet>
          <section className="global-container">
            <ProjectDetailsHeader
              title={project.attributes.title}
              price={project.attributes.price}
              isCompleted={project.attributes.isCompleted}
            />
          </section>
          <section className="overflow-x-hidden pr-[2rem] pl-[2rem] md:pl-[0rem] md:pr-[0rem]">
            {isMobile ? (
              <MobileProjectGallery
                videoUrl={project.attributes.videoLink}
                images={project.attributes?.images?.data}
                floorPlans={project.attributes?.floorPlans?.data}
              />
            ) : (
              <ProjectGallery
                videoUrl={project.attributes.videoLink}
                images={project.attributes?.images?.data}
                floorPlans={project.attributes?.floorPlans?.data}
              />
            )}
          </section>
          <section className="global-container">
            <ProjectInfo
              info={project.attributes.description}
              properties={project.attributes.properties}
            />
            {project.attributes.DevelopmentUpdateAvailable && (
              <DevelopmentUpdatesBtn projectId={project.id} />
            )}
            <ExploreMoreProjects />
          </section>
        </>
      )}
    </>
  );
};

export default ProjectDetails;
